import React, { useState, useEffect, lazy, Suspense } from 'react';
import { db } from '../../../../firebase/config';
import { collectionGroup, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

// Estilos compartidos (Movidos arriba de las constantes lazy para respetar ESLint import/first)
import { 
    getPersonaChipClass, 
    estadoCotizacionArt, 
    tipoGestionArt 
} from '../../../../utils/styles';

// --- IMPORTACIÓN DINÁMICA / LAZY LOADING (Optimización de Rendimiento de Hilo Principal) ---
// Evita que el navegador compile de golpe motores pesados como @react-pdf/renderer al hacer clic en el ojito
const DetalleCotizacionArt = lazy(() => import('../../TablerosGestion/ART/DetalleCotizaciones/DetalleCotizacionArt'));
const DetalleAumentoArt = lazy(() => import('../../TablerosGestion/ART/DetalleAumentos/DetalleAumentoArt'));
const FormularioCotizacionArt = lazy(() => import('../../../Formularios/FormularioCotizacionArt'));

// --- HELPERS DE FORMATO ---
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'string' ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : value;
    if (isNaN(num)) return '-';
    
    return new Intl.NumberFormat('es-AR', { 
        style: 'currency', 
        currency: 'ARS',
        minimumFractionDigits: 2 
    }).format(num || 0);
};

// Iconos
const IconEye = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>;
const IconEdit = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>;
const IconFileSearch = () => <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>;

// --- SKELETON DE ESPERA DE CARGA (UX PREMIUM) ---
const LoadingSkeleton = () => (
    <div className="p-8 space-y-6 bg-slate-50/50 rounded-2xl border border-slate-100 animate-pulse text-left">
        <div className="flex justify-between items-center pb-4 border-b border-slate-100">
            <div className="space-y-2">
                <div className="h-6 w-48 bg-slate-200 rounded-lg"></div>
                <div className="h-4 w-32 bg-slate-200 rounded-lg"></div>
            </div>
            <div className="h-10 w-28 bg-slate-200 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 bg-slate-200 rounded-2xl"></div>
            <div className="h-32 bg-slate-200 rounded-2xl"></div>
            <div className="h-32 bg-slate-200 rounded-2xl"></div>
        </div>
    </div>
);

const ContratoCotizaciones = ({ contrato }) => {
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCotizacion, setSelectedCotizacion] = useState(null);
    const [editingCotizacion, setEditingCotizacion] = useState(null);
    const [entityData, setEntityData] = useState(null); // Estado para hidratar cliente original al editar

    const fetchCotizacionesVinculadas = async () => {
        if (!contrato?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            // Consulta 1: Cotizaciones vinculadas directamente al contrato actual
            const q1 = query(
                collectionGroup(db, 'cotizaciones_art'),
                where('contratoVinculadoId', '==', contrato.id)
            );

            // Consulta 2: Cotizaciones que dieron origen a este contrato
            const q2 = query(
                collectionGroup(db, 'cotizaciones_art'),
                where('contratoOrigenId', '==', contrato.id)
            );

            const [snapshotLinked, snapshotOrigin] = await Promise.all([
                getDocs(q1),
                getDocs(q2)
            ]);

            // Unificamos las consultas en memoria usando un Map para evitar duplicados
            const uniqueCotizacionesMap = new Map();

            snapshotLinked.docs.forEach(docSnap => {
                uniqueCotizacionesMap.set(docSnap.id, {
                    id: docSnap.id,
                    ...docSnap.data(),
                    path: docSnap.ref.path
                });
            });

            snapshotOrigin.docs.forEach(docSnap => {
                uniqueCotizacionesMap.set(docSnap.id, {
                    id: docSnap.id,
                    ...docSnap.data(),
                    path: docSnap.ref.path
                });
            });

            const mergedList = Array.from(uniqueCotizacionesMap.values());
            mergedList.sort((a, b) => new Date(b.fechaCarga) - new Date(a.fechaCarga));
            
            setCotizaciones(mergedList);
        } catch (error) {
            console.error("Error buscando cotizaciones vinculadas:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCotizacionesVinculadas();
    }, [contrato?.id]);

    const handleEdit = async (cot) => {
        try {
            setLoading(true);

            if (!cot.path) {
                throw new Error("El documento no tiene un path de referencia válido.");
            }

            const parts = cot.path.split('/');
            const collectionPath = `public/data/${parts[2]}`; // 'public/data/clientes' o 'public/data/prospectos'
            const entityId = parts[3]; // El ID real del cliente o prospecto madre

            const entityRef = doc(db, collectionPath, entityId);
            const entitySnap = await getDoc(entityRef);

            if (entitySnap.exists()) {
                setEntityData({ id: entitySnap.id, ...entitySnap.data() });
            } else {
                setEntityData({
                    id: entityId,
                    nombre: cot.entidadNombre || '',
                    cuit: cot.entidadCuit || '',
                    dni: cot.entidadDni || '',
                    persona: cot.entidadCuit ? 'JURIDICA' : 'FISICA'
                });
            }
            setEditingCotizacion(cot);
        } catch (error) {
            console.error("Error al obtener datos reales de la entidad:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSuccess = (msg) => {
        setEditingCotizacion(null);
        setEntityData(null);
        fetchCotizacionesVinculadas(); 
    };

    // --- MODO EDICIÓN ---
    if (editingCotizacion && entityData) {
        return (
            <Suspense fallback={<LoadingSkeleton />}>
                <div className="animate-fade-in py-2">
                    <div className="mb-6 flex justify-between items-center">
                        <button 
                            onClick={() => { setEditingCotizacion(null); setEntityData(null); }} 
                            className="text-slate-500 hover:text-slate-800 flex items-center gap-2 text-sm font-bold transition-colors"
                        >
                            ← Cancelar edición
                        </button>
                        <h4 className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Editando Registro</h4>
                    </div>
                    
                    <FormularioCotizacionArt 
                        initialData={editingCotizacion}
                        entityData={entityData}
                        isProspect={editingCotizacion.tipoEntidad === 'PROSPECTO'}
                        onSuccess={handleSuccess}
                        onCancel={() => { setEditingCotizacion(null); setEntityData(null); }}
                    />
                </div>
            </Suspense>
        );
    }

    // --- VISTA DETALLE CON RUTEO DINÁMICO ---
    if (selectedCotizacion) {
        // Evaluamos si el tipo de gestión de la negociación corresponde a un aumento de tarifa
        const esAumentoDeTarifa = selectedCotizacion.tipoGestion === 'Aumento de Tarifa';

        return (
            <div className="animate-fade-in bg-gray-50 -m-6 p-6 rounded-b-xl min-h-[500px]">
                <div className="mb-4">
                    <button 
                        onClick={() => setSelectedCotizacion(null)} 
                        className="text-slate-500 hover:text-slate-800 flex items-center gap-2 text-sm font-bold transition-colors"
                    >
                        ← Volver a la lista de cotizaciones
                    </button>
                </div>
                
                {/* ◄◄◄ SUSPENSE WRAPPER: Libera el hilo de renderizado principal del PDF oficial de inmediato */}
                <Suspense fallback={<LoadingSkeleton />}>
                    {esAumentoDeTarifa ? (
                        /* Renderiza la pantalla específica del flujo de aumentos */
                        <DetalleAumentoArt 
                            cotizacion={selectedCotizacion} 
                            onBack={() => setSelectedCotizacion(null)}
                        />
                    ) : (
                        /* Renderiza la pantalla estándar del flujo de nuevas emisiones/recotizaciones */
                        <DetalleCotizacionArt 
                            cotizacion={selectedCotizacion}
                            onBack={() => setSelectedCotizacion(null)}
                        />
                    )}
                </Suspense>
            </div>
        );
    }

    // --- VISTA LISTA ---
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800">Historial de Cotizaciones ART vinculadas</h3>
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-200">
                    {cotizaciones.length} Registros
                </span>
            </div>

            {loading ? (
                <div className="flex justify-center p-12">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : cotizaciones.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                    <div className="flex flex-col items-center">
                        <IconFileSearch />
                        <p className="text-slate-500 font-medium">No hay cotizaciones vinculadas a este contrato.</p>
                        <p className="text-slate-400 text-sm mt-1">Al crear una nueva cotización ART, selecciona este contrato para vincularla.</p>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Fecha</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">N° Cotización</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Gestión</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Estado</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap text-center">Empleados</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap text-right">Masa Salarial</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Vendedor</th>
                                    <th className="p-4 text-xs font-semibold text-slate-500 uppercase text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {cotizaciones.map((cot) => (
                                    <tr key={cot.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="p-4 text-sm text-slate-700 whitespace-nowrap">
                                            {cot.fechaCarga ? new Date(cot.fechaCarga).toLocaleDateString('es-AR') : '-'}
                                        </td>
                                        <td className="p-4 text-sm text-slate-900 font-mono font-bold whitespace-nowrap">
                                            {cot.numeroCotizacion || <span className="text-gray-400 italic text-xs">S/N</span>}
                                        </td>
                                        <td className="p-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm border ${tipoGestionArt(cot.tipoGestion)}`}>
                                                {cot.tipoGestion || '-'}
                                            </span>
                                        </td>
                                        <td className="p-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm border ${estadoCotizacionArt(cot.estado)}`}>
                                                {cot.estado}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-slate-700 whitespace-nowrap text-center font-bold">
                                            {cot.capitas || '0'}
                                        </td>
                                        <td className="p-4 text-sm text-slate-700 whitespace-nowrap font-mono font-bold text-right">
                                            {formatCurrency(cot.masaSalarial)}
                                        </td>
                                        <td className="p-4 whitespace-nowrap">
                                            {cot.vendedor ? (
                                                <div className={`flex items-center gap-2 px-2 py-0.5 rounded-full w-fit ${getPersonaChipClass(cot.vendedor)}`}>
                                                    <span className="text-xs font-bold">{cot.vendedor}</span>
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="p-4 text-right whitespace-nowrap">
                                            <div className="flex justify-end gap-2">
                                                <button 
                                                    onClick={() => setSelectedCotizacion(cot)} 
                                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all" 
                                                    title="Ver Detalle"
                                                >
                                                    <IconEye />
                                                </button>
                                                <button 
                                                    onClick={() => handleEdit(cot)} 
                                                    className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-all" 
                                                    title="Editar Cotización"
                                                >
                                                    <IconEdit />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContratoCotizaciones;